/**
 * @license
 * Copyright (c) 2021-present, FriendliAI Inc. All rights reserved.
 */

import { Text, useMantineTheme, Divider, useCombobox, rem } from '@mantine/core';
import { RiAddLine, RiArrowRightLine } from '@remixicon/react';
import type { Dayjs } from 'dayjs';
import type { ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import Paths from '@fai/friendli-suite/features/routes/paths';
import type { SimpleUser } from '@fai/friendli-suite/features/types/user-settings';
import { openCreateProjectModal } from '@fai/friendli-suite/views/dedicated-endpoints/projects/list/create-project-modal';
import type OptionItem from '@fai/friendli-suite/views/layout/product/option-item';
import { TextVariants } from '@fai/ui/components/typography/variants';

interface OptionItemElement {
  type: 'optionItem';
  props: ComponentProps<typeof OptionItem>;
}

interface OptionListElement {
  type: 'optionList';
  props: OptionItemElement[];
}

interface JSXElement {
  type: 'jsx';
  Component: React.ReactElement;
}

export type ProjectDetailComboBoxItems = Array<OptionItemElement | OptionListElement | JSXElement>;

export const useProjectDetailComboxStore = () => {
  const projectDetailComboBox = useCombobox({
    onDropdownClose: () => projectDetailComboBox.resetSelectedOption(),
    onDropdownOpen: (eventSource) => {
      if (eventSource === 'keyboard') {
        projectDetailComboBox.selectActiveOption();
      } else {
        projectDetailComboBox.updateSelectedOptionIndex('active');
      }
    },
  });

  const handleProjectDetailComboBoxChange = (value: string) => {
    projectDetailComboBox.closeDropdown();
    projectDetailComboBox.updateSelectedOptionIndex('active');
  };

  return { projectDetailComboBox, handleProjectDetailComboBoxChange };
};

export const useProjectDetailComboBoxItems = ({
  teamId,
  isTeamOwner,
  projectList,
}: {
  teamId: string;
  isTeamOwner: boolean;
  projectList?: {
    id: string;
    name?: string | null;
    endpointCount?: number | null;
    modelCount?: number | null;
    updatedAt?: Dayjs | null;
    members?: Array<SimpleUser> | null;
  }[];
}): ProjectDetailComboBoxItems => {
  const theme = useMantineTheme();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const parsedProjectList: OptionItemElement[] =
    projectList
      ?.map((project) => ({
        id: project.id,
        name: project.name,
      }))
      .map((project) => ({
        type: 'optionItem',
        props: {
          label: project.name,
          value: project.id,
          onClick: () => {
            navigate(Paths.team(teamId).dedicatedEndpoints.project.detail(project.id).default);
          },
        },
      })) ?? [];

  const teamOwnerOnlyMenu: ProjectDetailComboBoxItems = isTeamOwner
    ? [
        {
          type: 'jsx',
          Component: <Divider mb={rem(4)} />,
        },
        {
          type: 'optionItem',
          props: {
            label: t('dedicated_endpoints.layout.create_project'),
            value: 'createProject',
            leftSlot: <RiAddLine size={16} />,
            onClick: openCreateProjectModal,
          },
        },
      ]
    : [];

  return [
    {
      type: 'jsx',
      Component: (
        <Text
          c={theme.pf.color.fg.neutral.subtle}
          pb={theme.pf.layout['3xs']}
          pl={theme.pf.layout.xs}
          pt={theme.pf.layout.xs}
          variant={TextVariants.Title2Xs}
        >
          {t('dedicated_endpoints.layout.projects')}
        </Text>
      ),
    },
    {
      type: 'optionList',
      props: [
        ...parsedProjectList,
        {
          type: 'optionItem',
          props: {
            onClick: () => {
              navigate(Paths.team(teamId).dedicatedEndpoints.project.list);
            },
            label: t('dedicated_endpoints.layout.all_projects'),
            value: 'allProjects',
            rightSlot: <RiArrowRightLine size={16} />,
          },
        },
      ],
    },

    ...teamOwnerOnlyMenu,
  ];
};
